/**
 * THE TRANSCRIPT AS A THREAD — a role badge, the words, the arrival time, and our
 * own capture gaps marked in the reading order.
 *
 * LIFTED OUT OF `pages/SessionDetail.tsx` UNCHANGED so that the session viewer
 * (`/session-viewer`, embedded in a customer's ServiceNow form) renders the same
 * component rather than a copy of it. A copy was the alternative and it is the
 * wrong one: the viewer's whole requirement is that it look EXACTLY like the
 * session page, which a second implementation stops being true of the first time
 * either side is edited. The page still owns the sections around it.
 *
 * NOTHING HERE FETCHES. It takes rows and renders them, so a caller holding a
 * webhook payload can render it as readily as one holding an API response — the
 * viewer does exactly that.
 *
 * THE ORDERING LIVES NEXT DOOR, in `transcript-order.ts` — pure functions from
 * rows to rows, with no React in reach, so a test can bundle them alone. This
 * file is the rendering half.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRightIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { fmtClock, fmtMs } from '@/components/state-pill'
import {
  buildTranscript,
  sessionEndMs,
  toolDump,
  type TranscriptMarker,
  type TranscriptToolCall,
  type TranscriptTurn,
} from '@/components/session/transcript-order'

export type { TranscriptMarker, TranscriptToolCall, TranscriptTurn }

/**
 * The transcript as a thread: a role badge, the words, and the arrival time on
 * the right — modelled on voice2me's session view at the product owner's
 * direction. Tool calls are rows in the flow (name + a disclosure with the full
 * arguments and result), and our own capture gaps stay marked IN the reading
 * order, because a missing marker reads as continuous speech.
 *
 * `playheadEpochMs` is the audio player's position translated onto the
 * session's clock; the row being spoken is highlighted and kept in view. The
 * match is "latest row that had started by now" — turns carry no end time, so
 * this errs toward the row a listener just heard rather than none.
 */
export function TranscriptThread({
  turns,
  toolCalls,
  events,
  endedAt,
  playheadEpochMs,
}: {
  turns: TranscriptTurn[]
  toolCalls: TranscriptToolCall[]
  events: TranscriptMarker[]
  endedAt: string | null
  playheadEpochMs: number | null
}) {
  const entries = useMemo(
    () => buildTranscript(turns, toolCalls, events, sessionEndMs(events, endedAt)),
    [turns, toolCalls, events, endedAt],
  )
  const [openTools, setOpenTools] = useState<ReadonlySet<string>>(new Set())
  const rowRefs = useRef(new Map<number, HTMLDivElement>())

  /**
   * When each row should LIGHT UP, which is not the same as its anchor.
   *
   * The anchors are the honest sort keys, but two of them sit at the wrong end
   * for a listener: a user line is anchored at speech-STOPPED, so highlighting
   * from the anchor lit the row only after the words had finished playing —
   * "the transcript is not highlighted properly", per the product owner. The
   * fix: a row becomes active when the PREVIOUS row stops (the assistant's
   * playout-stopped anchor, where observed), not when its own anchor lands.
   * Rows whose predecessor has no stop anchor keep their own.
   */
  const highlightAt = useMemo(() => {
    const hAt = entries.map((e) => e.at)
    let prevEnd: number | null = null
    entries.forEach((e, i) => {
      if (e.kind === 'marker') return
      // A row with no clock cannot be adjusted onto one, and must not drag
      // `prevEnd` with it — on an unclocked payload every row is null and the
      // whole pass is a no-op, which is the correct outcome.
      if (e.at === null) return
      if (e.kind === 'said' && e.who === 'user' && prevEnd != null && prevEnd < e.at) {
        hAt[i] = prevEnd
      }
      prevEnd = e.until != null && e.until > e.at ? e.until : e.at
    })
    return hAt
  }, [entries])

  let activeIdx = -1
  if (playheadEpochMs != null) {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i]
      if (!e || e.kind === 'marker') continue
      // NEVER highlight a row we have no time for. Without this an unclocked
      // payload highlights every row at once (null coerces to 0, which is always
      // "already started"), which reads as the whole call being spoken at once.
      const at = highlightAt[i] ?? e.at
      if (at !== null && at <= playheadEpochMs) activeIdx = i
    }
  }

  useEffect(() => {
    if (activeIdx < 0) return
    rowRefs.current.get(activeIdx)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [activeIdx])

  if (entries.length === 0) {
    return (
      <p className="empty text-sm">
        No transcript was captured. Transcripts arrive on the sideband as
        <span className="mono"> conversation.item.input_audio_transcription.completed </span>
        and <span className="mono">response.output_audio_transcript.done</span>, so an empty
        transcript means our socket was not attached — NOT that nothing was said.
      </p>
    )
  }

  const toggleTool = (id: string) =>
    setOpenTools((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const rowRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(i, el)
    else rowRefs.current.delete(i)
  }

  return (
    <div className="flex flex-col gap-0.5">
      {entries.map((e, i) => {
        if (e.kind === 'marker') {
          return (
            <div
              key={`m-${e.event.id}`}
              className="my-1 flex items-center gap-2 rounded-md border border-dashed border-ev-warn-border bg-ev-warn-bg px-3 py-1.5 text-xs text-ev-warn"
              title={
                typeof e.event.detail === 'object' && e.event.detail !== null
                  ? JSON.stringify(e.event.detail)
                  : undefined
              }
            >
              <span className="mono shrink-0">{e.at === null ? '' : fmtClock(e.at)}</span>
              <span className="font-semibold">{e.event.kind}</span>
              <span className="text-ev-warn/80">
                our capture was interrupted here — anything said during this interval is not
                in the transcript below
              </span>
            </div>
          )
        }

        const active = i === activeIdx

        if (e.kind === 'tool') {
          const c = e.call
          const open = openTools.has(c.callId)
          return (
            <div
              key={`t-${c.callId}`}
              ref={rowRef(i)}
              className={cn('flex items-start gap-3 rounded-md px-2 py-1.5', active && 'bg-accent')}
            >
              <Badge variant="outline" className="w-12 shrink-0 justify-center">
                tool
              </Badge>
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => toggleTool(c.callId)}
                  className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 text-left"
                  aria-expanded={open}
                >
                  <span className="mono text-sm">{c.name}</span>
                  <ChevronRightIcon
                    className={cn('size-3.5 text-muted-foreground transition-transform', open && 'rotate-90')}
                    aria-hidden
                  />
                  {c.ok === false && <span className="text-xs font-semibold text-ev-bad">failed</span>}
                  {c.ok == null && (
                    <span
                      className="text-xs text-muted-foreground"
                      title="No output was recorded for this call. A tool that sends nothing leaves the model waiting forever, so this is a bug in the tool, not a state."
                    >
                      no output
                    </span>
                  )}
                  {c.orphaned && (
                    <span
                      className="pill pill-plain"
                      title="The response this call belonged to was cancelled — the user barged in while the tool was running. The side effect still happened; the model never saw the result."
                    >
                      orphaned
                    </span>
                  )}
                  {c.durationMs != null && (
                    <span className="num text-xs text-muted-foreground">{fmtMs(c.durationMs)}</span>
                  )}
                </button>
                {open && (
                  <div className="mt-1 flex flex-col gap-1">
                    {c.errorMessage && <p className="text-xs text-ev-bad">{c.errorMessage}</p>}
                    {/*
                      Arguments are MODEL output and results are TOOL output;
                      neither is evidence that anything was authorised. Same
                      rule as the table in the tech stats drawer.
                    */}
                    <pre className="mono overflow-x-auto rounded border bg-muted/50 p-2 text-xs">
                      {toolDump(c)}
                    </pre>
                  </div>
                )}
              </div>
              <span className="mono shrink-0 pt-0.5 text-xs text-muted-foreground">
                {e.at === null ? '' : fmtClock(e.at)}
              </span>
            </div>
          )
        }

        const isUser = e.who === 'user'
        return (
          <div
            key={`s-${e.who}-${e.n}-${i}`}
            ref={rowRef(i)}
            className={cn('flex items-start gap-3 rounded-md px-2 py-1.5', active && 'bg-accent')}
          >
            <Badge variant={isUser ? 'default' : 'secondary'} className="w-12 shrink-0 justify-center">
              {isUser ? 'user' : 'agent'}
            </Badge>
            <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm">{e.text}</p>
            <span className="mono shrink-0 pt-0.5 text-xs text-muted-foreground">
              {e.at === null ? '' : fmtClock(e.at)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
